import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { MaintenanceTimerCompletionService } from '@artemis-software/wr-api';
import { toSignal } from '@angular/core/rxjs-interop';
import { firstValueFrom, map } from 'rxjs';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { environment } from 'src/environments/environment';

@Component({
  standalone: false,
  selector: 'wr-maintenance-timer-history-detail-page',
  templateUrl: './maintenance-timer-history-detail-page.component.html',
  styleUrls: ['./maintenance-timer-history-detail-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MaintenanceTimerHistoryDetailPageComponent {

  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly service = inject(MaintenanceTimerCompletionService);
  readonly http = inject(HttpClient);

  readonly id = toSignal(
    this.route.paramMap.pipe(map(p => p.get('id'))),
    { initialValue: null }
  );

  readonly detail = asyncComputed(() => {
    const id = this.id();
    if (!id) return null;
    return this.service.findById(id);
  }, null);

  goBack() {
    window.history.back();
  }

  async downloadProtocolPdf(protocolId: string): Promise<void> {
    try {
      const blob = await firstValueFrom(
        this.http.get(`${environment.apiUrl}/api/protocol/${protocolId}/exportPdf`, {
          responseType: 'blob',
        })
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `protocol-${protocolId}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download protocol PDF:', error);
    }
  }

  async downloadPdf() {
    const id = this.id();
    if (!id) return;

    try {
      const response = await firstValueFrom(
        this.http.post(`${environment.apiUrl}/api/maintenanceTimerCompletionPdf/generate`,
          { maintenanceTimerCompletionId: id },
          {
            responseType: 'blob',
            headers: {
              'Accept': 'application/pdf'
            }
          }
        )
      );

      const url = window.URL.createObjectURL(response);
      const a = document.createElement('a');
      a.href = url;
      a.download = `maintenance-timer-completion-${id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download PDF:', error);
    }
  }
}
